import React, { useRef, useState, useEffect, useCallback } from "react";

import {
    DmsPage,
    DmsButtonGroup,
    DmsPermissions,
    DmsTable,
    DmsAltBox,
    DmsTabber,
    DmsFlexContainer,
    fetchData,
    HTTP_STATUS,
    intl
} from "dmsbasereact";
import { Typography, Chip, Box } from "@mui/material";

import { Permissions } from "../../constants/Permissions";
import { ReturnStatus } from "../../constants/ReturnStatus";
import { DmsCreateReturnButton } from "../../components/DmsCreateReturnButton/DmsCreateReturnButton";
import { DmsReturnEditButton } from "../../components/DmsReturnEditButton/DmsReturnEditButton";
import { DmsReturnViewButton } from "../../components/DmsReturnViewButton/DmsReturnViewButton";
import { useReturnReasons } from '../../constants/ReturnReasons';
import { useTreatmentDiscontinuationOptions } from '../../constants/TreatmentDiscontinuationEnum';
import { useQualityIssueOptions } from '../../constants/QualityIssues';


/*
* Description: This component is the DMS return page for pharmacists
* Props: none
*/
export const DmsReturnPage = () => {
    const ReturnReasons = useReturnReasons();
    const QualityIssueOptions = useQualityIssueOptions();
    const TreatmentDiscontinuationOptions = useTreatmentDiscontinuationOptions();

    const pendingTable = useRef(null);
    const completedTable = useRef(null);
    const [reasonOptions, setReasonOptions] = useState([]);

    // Fetch return reason options from backend for filters (with translations)
    useEffect(() => {
        const fetchReasonOptions = async () => {
            try {
                const response = await fetchData('/return/management/reason-options', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                    // Map backend options to translated labels
                    const translatedOptions = response.data.data.map(opt => ({
                        value: opt.value,
                        label: ReturnReasons[opt.value] || opt.label
                    }));
                    setReasonOptions(translatedOptions);
                }
            } catch (error) {
                console.error("Failed to load reason options:", error);
            }
        };
        fetchReasonOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getReasonDetailLabel = useCallback((row) => {
        const reasonDetails = row?.reason_details;
        if (!reasonDetails) return '-';

        if (row?.return_reason?.name === "QUALITY_ISSUE") {
            return QualityIssueOptions.find(opt => opt.value === reasonDetails)?.label || reasonDetails;
        }

        if (row?.return_reason?.name === "TREATMENT_DISCONTINUATION") {
            return TreatmentDiscontinuationOptions.find(opt => opt.value === reasonDetails)?.label || reasonDetails;
        }

        return reasonDetails;
    }, [QualityIssueOptions, TreatmentDiscontinuationOptions]);

    const pendingHeader = [
        {
            label: intl.get("ID").d("ID"),
            column_name: "pk",
            filter: true,
            width: "80px"
        },
        {
            label: intl.get("Institution").d("Institution"),
            column_name: "organization_name",
            filter: true
        },
        {
            label: intl.get("box_count").d("Box Count"),
            column_name: "quantity",
            filter: false
        },
        {
            label: intl.get("ReturnReason").d("Return Reason"),
            column_name: "return_reason_name",
            filter: true,
            control: "select",
            options: reasonOptions,
            element: (row) => {
                const reasonName = row?.return_reason?.name || row?.return_reason_name;
                if (reasonName && ReturnReasons[reasonName]) {
                    return ReturnReasons[reasonName];
                }
                return reasonName || '-';
            }
        },
        {
            label: intl.get("Detail").d("Detail"),
            column_name: "reason_details",
            filter: false,
            element: (row) => getReasonDetailLabel(row)
        },
        {
            label: intl.get("InvoiceNumber").d("Invoice Number"),
            column_name: "invoice_number",
            filter: true
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status_label",
            filter: true,
            element: (row) => {
                const statusValue = typeof row.status === 'object' ? row.status.value : row.status;
                return (
                    <Chip
                        label={ReturnStatus.getText(statusValue)}
                        color={ReturnStatus.getColor(statusValue)}
                        size="small"
                    />
                );
            }
        },
        {
            label: intl.get("LastUpdated").d("Last Updated"),
            column_name: "updated_on",
            filter: false,
            element: (row) => row?.updated_on ? new Date(row.updated_on).toLocaleDateString('pt-BR') : '-'
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsReturnViewButton row={row} />
                    <DmsReturnEditButton table={pendingTable} row={row} />
                </DmsButtonGroup>
            )
        },
    ];

    const completedHeader = [
        {
            label: intl.get("ID").d("ID"),
            column_name: "pk",
            filter: true,
            width: "80px"
        },
        {
            label: intl.get("Institution").d("Institution"),
            column_name: "organization_name",
            filter: true
        },
        {
            label: intl.get("box_count").d("Box Count"),
            column_name: "quantity",
            filter: false
        },
        {
            label: intl.get("ReturnReason").d("Return Reason"),
            column_name: "return_reason_name",
            filter: true,
            control: "select",
            options: reasonOptions,
            element: (row) => {
                const reasonName = row?.return_reason?.name || row?.return_reason_name;
                if (reasonName && ReturnReasons[reasonName]) {
                    return ReturnReasons[reasonName];
                }
                return reasonName || '-';
            }
        },
        {
            label: intl.get("Detail").d("Detail"),
            column_name: "reason_details",
            filter: false,
            element: (row) => getReasonDetailLabel(row)
        },
        {
            label: intl.get("InvoiceNumber").d("Invoice Number"),
            column_name: "invoice_number",
            filter: true
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status_label",
            filter: false,
            element: (row) => {
                const statusValue = typeof row.status === 'object' ? row.status.value : row.status;
                return (
                    <Chip
                        label={ReturnStatus.getText(statusValue)}
                        color={ReturnStatus.getColor(statusValue)}
                        size="small"
                    />
                );
            }
        },
        {
            label: intl.get("LastUpdated").d("Last Updated"),
            column_name: "updated_on",
            filter: false,
            element: (row) => row?.updated_on ? new Date(row.updated_on).toLocaleDateString('pt-BR') : '-'
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "80px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsReturnViewButton row={row} />
                </DmsButtonGroup>
            )
        },
    ];

    const pendingToolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsPermissions permissions={[Permissions.RETURN_CREATE]}>
                    <DmsCreateReturnButton
                        header={pendingHeader}
                        table={pendingTable}
                        title={intl.get("CreateReturn").d("Create Return")}
                        url={'/return/'}
                    />
                </DmsPermissions>
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    const tabs = [
        {
            title: intl.get("PendingReturns").d("Pending"),
            component: (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("PharmacistPendingReturnsDescription").d("Returns pending collection or in progress.")}
                        </Typography>
                    </Box>
                    <DmsTable
                        header={pendingHeader}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={pendingTable}
                        url={'/return/pharmacist/pending/table'}
                        toolbar={pendingToolbar}
                    />
                </>
            )
        },
        {
            title: intl.get("CompletedReturns").d("Completed"),
            component: (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("PharmacistCompletedReturnsDescription").d("View completed returns and their documents.")}
                        </Typography>
                    </Box>
                    <DmsTable
                        header={completedHeader}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={completedTable}
                        url={'/return/pharmacist/completed/table'}
                    />
                </>
            )
        }
    ];

    return (
        <DmsPage name="Return">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                        {intl.get("Returns").d("Returns")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTabber tabs={tabs} />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};
